// src/hr/kit/Seg.js -- segmented choice with radio-group semantics.
// One tab stop for the group; arrow keys (and Home / End) move the selection and focus, skipping
// disabled options, wrapping at the ends.
import React, { useRef } from 'react';
import { cx } from './util';

const normalize = (options) => (options || []).map((o) => (
  o !== null && typeof o === 'object' ? o : { value: o, label: String(o) }
));

// options: [{ value, label, disabled? }] or plain strings. label: accessible name of the group
// (or labelledBy: id of a visible label).
export function Seg({ options, value, onChange, label, labelledBy, size = 'md', className }) {
  const items = normalize(options);
  const refs = useRef([]);
  const selected = items.findIndex((o) => o.value === value);
  const tabStop = selected >= 0 && !items[selected].disabled ? selected : items.findIndex((o) => !o.disabled);

  const step = (from, dir) => {
    let i = from;
    for (let n = 0; n < items.length; n += 1) {
      i = (i + dir + items.length) % items.length;
      if (!items[i].disabled) return i;
    }
    return from;
  };

  const choose = (i) => {
    if (i < 0 || !items[i] || items[i].disabled) return;
    if (items[i].value !== value && onChange) onChange(items[i].value);
    if (refs.current[i]) refs.current[i].focus();
  };

  const onKeyDown = (e, i) => {
    const keys = {
      ArrowRight: () => step(i, 1),
      ArrowDown: () => step(i, 1),
      ArrowLeft: () => step(i, -1),
      ArrowUp: () => step(i, -1),
      Home: () => step(-1, 1),
      End: () => step(items.length, -1)
    };
    if (!keys[e.key]) return;
    e.preventDefault();
    choose(keys[e.key]());
  };

  return (
    <div
      role="radiogroup"
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      className={cx('hrp-seg', size === 'sm' && 'hrp-seg--sm', className)}
    >
      {items.map((o, i) => (
        <button
          key={String(o.value)}
          ref={(el) => { refs.current[i] = el; }}
          type="button"
          role="radio"
          aria-checked={i === selected}
          tabIndex={i === tabStop ? 0 : -1}
          disabled={o.disabled}
          className="hrp-seg__opt"
          onClick={() => choose(i)}
          onKeyDown={(e) => onKeyDown(e, i)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default Seg;
